import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  Camera,
  CheckCircle2,
  FileText,
  RefreshCw,
  Upload,
  X,
} from 'lucide-react';
import { auth } from '../../lib/firebase';
import { apiFetch as fetch } from '../../lib/apiConfig';
import type { KycUserNotification } from '../../types/kyc.types';

interface UserKycRejectionNotificationsBannerProps {
  userId?: string;
  onResubmitted?: () => void;
}

export const UserKycRejectionNotificationsBanner: React.FC<
  UserKycRejectionNotificationsBannerProps
> = ({ userId, onResubmitted }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<KycUserNotification[]>([]);
  const [rejectedDocs, setRejectedDocs] = useState<any[]>([]);
  const [rejectedSelfie, setRejectedSelfie] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Inline replacement & resubmission state
  const [activeReplaceKey, setActiveReplaceKey] = useState<string | null>(null);
  const [replaceFrontDataUrl, setReplaceFrontDataUrl] = useState<string | null>(null);
  const [replaceBackDataUrl, setReplaceBackDataUrl] = useState<string | null>(null);
  const [replaceSelfieDataUrl, setReplaceSelfieDataUrl] = useState<string | null>(null);
  const [isSubmittingReplace, setIsSubmittingReplace] = useState<boolean>(false);
  const [replaceFeedback, setReplaceFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const loadUserKycNotifications = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    setIsLoading(true);
    try {
      const idToken = await currentUser.getIdToken();
      const res = await fetch('/api/kyc/documents', {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (!data.success) return;

      const notifs: KycUserNotification[] = Array.isArray(data.notifications)
        ? data.notifications
        : [];
      setNotifications(notifs);

      const docs = Array.isArray(data.documents) ? data.documents : [];
      setRejectedDocs(
        docs.filter(
          (d: any) =>
            String(d.status || '').toUpperCase() === 'REJECTED' ||
            String(d.status || '').toUpperCase() === 'REUPLOAD_REQUIRED'
        )
      );

      if (
        data.selfie &&
        (String(data.selfie.status || '').toUpperCase() === 'REJECTED' ||
          String(data.selfie.status || '').toUpperCase() === 'REUPLOAD_REQUIRED')
      ) {
        setRejectedSelfie(data.selfie);
      } else {
        setRejectedSelfie(null);
      }
    } catch {
      // Ignore background fetch errors when user is not yet authenticated
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUserKycNotifications();
    const unsub = auth.onAuthStateChanged((u) => {
      if (u) loadUserKycNotifications();
    });
    return () => unsub();
  }, [userId]);

  const handleMarkNotificationRead = async (notificationId: string) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      const idToken = await currentUser.getIdToken();
      await fetch(`/api/kyc/notifications/${encodeURIComponent(notificationId)}/read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
    } catch {
      // Ignore
    }
  };

  const readFileAsDataUrl = (file: File, onDone: (url: string) => void) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (result) onDone(result);
    };
    reader.readAsDataURL(file);
  };

  const handleResubmitReplacement = async (
    targetType: 'DOCUMENT' | 'SELFIE',
    documentType: string
  ) => {
    setReplaceFeedback(null);
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setReplaceFeedback({ type: 'error', message: 'Please sign in to resubmit.' });
      return;
    }

    if (targetType === 'SELFIE' && !replaceSelfieDataUrl) {
      setReplaceFeedback({
        type: 'error',
        message: 'Please select a replacement selfie photo before resubmitting.',
      });
      return;
    }

    if (targetType === 'DOCUMENT' && !replaceFrontDataUrl) {
      setReplaceFeedback({
        type: 'error',
        message: `Please select a replacement front image for your ${documentType}.`,
      });
      return;
    }

    setIsSubmittingReplace(true);
    try {
      const idToken = await currentUser.getIdToken();
      const uploadPayload =
        targetType === 'SELFIE'
          ? {
              documentType: 'SELFIE',
              selfieBase64: replaceSelfieDataUrl,
            }
          : {
              documentType,
              frontBase64: replaceFrontDataUrl,
              backBase64: replaceBackDataUrl || undefined,
            };

      const uploadRes = await fetch('/api/kyc/documents/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(uploadPayload),
      });
      const uploadData = await uploadRes.json();
      if (!uploadRes.ok || !uploadData.success) {
        throw new Error(uploadData.error?.message || 'Failed to upload replacement.');
      }

      // Also call /api/kyc/submit to ensure status is UNDER_REVIEW for Admin Panel
      await fetch('/api/kyc/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ documentType: targetType === 'SELFIE' ? 'SELFIE' : documentType }),
      });

      setReplaceFeedback({
        type: 'success',
        message: `Corrected ${
          targetType === 'SELFIE' ? 'Selfie' : documentType
        } uploaded and resubmitted for Admin review.`,
      });
      setActiveReplaceKey(null);
      setReplaceFrontDataUrl(null);
      setReplaceBackDataUrl(null);
      setReplaceSelfieDataUrl(null);
      await loadUserKycNotifications();
      if (onResubmitted) onResubmitted();
    } catch (err: any) {
      setReplaceFeedback({
        type: 'error',
        message: err.message || 'Failed to resubmit replacement.',
      });
    } finally {
      setIsSubmittingReplace(false);
    }
  };

  const hasAlerts =
    notifications.length > 0 || rejectedDocs.length > 0 || Boolean(rejectedSelfie);

  if (!hasAlerts && !replaceFeedback) {
    return null;
  }

  return (
    <div
      className="mb-6 rounded-2xl bg-gradient-to-r from-rose-950/70 via-slate-900 to-slate-900 border border-rose-500/40 p-5 shadow-xl space-y-4"
      data-testid="user-kyc-rejection-notifications"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center text-rose-400 shrink-0">
            <Bell className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm sm:text-base font-black text-white flex items-center gap-2">
              <span>KYC Document / Selfie Rejection Notice</span>
              {notifications.filter((n) => !n.read).length > 0 && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-rose-600 text-white">
                  {notifications.filter((n) => !n.read).length} Action Required
                </span>
              )}
            </h3>
            <p className="text-xs text-slate-300">
              Please review the Admin rejection reason below, upload a corrected replacement, and resubmit for review.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={loadUserKycNotifications}
          className="px-2.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold flex items-center gap-1 cursor-pointer"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {replaceFeedback && (
        <div
          className={`p-3 rounded-xl border text-xs font-bold flex items-center justify-between ${
            replaceFeedback.type === 'success'
              ? 'bg-emerald-950/80 border-emerald-700 text-emerald-200'
              : 'bg-rose-950/80 border-rose-700 text-rose-200'
          }`}
        >
          <div className="flex items-center gap-2">
            {replaceFeedback.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
            )}
            <span>{replaceFeedback.message}</span>
          </div>
          <button
            type="button"
            onClick={() => setReplaceFeedback(null)}
            className="text-slate-400 hover:text-white"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Individual Rejection Items (Documents & Selfie) */}
      <div className="space-y-3">
        {rejectedDocs.map((doc) => {
          const replaceKey = `DOC_${doc.documentType}`;
          const isReplacing = activeReplaceKey === replaceKey;
          const requiresBack = doc.documentType !== 'PAN';
          return (
            <div
              key={doc.id || doc.documentType}
              className="p-4 rounded-xl bg-slate-950/90 border border-rose-500/40 space-y-3"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-rose-400" />
                  <span className="text-xs font-black text-white font-mono">
                    Rejected Item: {doc.documentType} Document
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-rose-500/20 text-rose-300 border border-rose-500/30">
                    {doc.status}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setActiveReplaceKey(isReplacing ? null : replaceKey)
                    }
                    className="px-3 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold transition cursor-pointer flex items-center gap-1.5"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    <span>{isReplacing ? 'Cancel' : `Replace & Resubmit ${doc.documentType}`}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/kyc-verification')}
                    className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold transition cursor-pointer"
                  >
                    Open KYC Page
                  </button>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-rose-950/50 border border-rose-800/60 text-xs text-rose-200">
                <span className="font-bold text-rose-300 block mb-0.5">
                  Admin Rejection Reason ({doc.documentType}):
                </span>
                <span>{doc.rejectionReason}</span>
              </div>

              {isReplacing && (
                <div className="p-3.5 rounded-xl bg-slate-900 border border-slate-700 space-y-3">
                  <div className="text-xs font-bold text-white">
                    Upload Corrected {doc.documentType} Replacement
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <label className="p-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-purple-500 cursor-pointer flex flex-col gap-1">
                      <span className="font-bold text-slate-300">
                        Front Side Image *
                      </span>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) readFileAsDataUrl(file, setReplaceFrontDataUrl);
                        }}
                        className="text-[11px] text-slate-400"
                      />
                      {replaceFrontDataUrl && (
                        <span className="text-[10px] text-emerald-400 font-mono">
                          ✓ Front replacement selected
                        </span>
                      )}
                    </label>

                    {requiresBack && (
                      <label className="p-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-purple-500 cursor-pointer flex flex-col gap-1">
                        <span className="font-bold text-slate-300">
                          Back Side Image (Optional / If Needed)
                        </span>
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) readFileAsDataUrl(file, setReplaceBackDataUrl);
                          }}
                          className="text-[11px] text-slate-400"
                        />
                        {replaceBackDataUrl && (
                          <span className="text-[10px] text-emerald-400 font-mono">
                            ✓ Back replacement selected
                          </span>
                        )}
                      </label>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={isSubmittingReplace}
                      onClick={() => handleResubmitReplacement('DOCUMENT', doc.documentType)}
                      className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold cursor-pointer flex items-center gap-1.5"
                    >
                      {isSubmittingReplace ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      )}
                      <span>Resubmit Corrected {doc.documentType} for Review</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {rejectedSelfie && (
          <div className="p-4 rounded-xl bg-slate-950/90 border border-rose-500/40 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Camera className="w-4 h-4 text-rose-400" />
                <span className="text-xs font-black text-white font-mono">
                  Rejected Item: Live Verification Selfie (SELFIE)
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-rose-500/20 text-rose-300 border border-rose-500/30">
                  {rejectedSelfie.status}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setActiveReplaceKey(
                      activeReplaceKey === 'SELFIE' ? null : 'SELFIE'
                    )
                  }
                  className="px-3 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold transition cursor-pointer flex items-center gap-1.5"
                >
                  <Upload className="w-3.5 h-3.5" />
                  <span>
                    {activeReplaceKey === 'SELFIE'
                      ? 'Cancel'
                      : 'Replace & Resubmit Selfie'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/kyc-verification')}
                  className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold transition cursor-pointer"
                >
                  Open KYC Page
                </button>
              </div>
            </div>

            <div className="p-3 rounded-lg bg-rose-950/50 border border-rose-800/60 text-xs text-rose-200">
              <span className="font-bold text-rose-300 block mb-0.5">
                Admin Rejection Reason (Selfie):
              </span>
              <span>{rejectedSelfie.rejectionReason}</span>
            </div>

            {activeReplaceKey === 'SELFIE' && (
              <div className="p-3.5 rounded-xl bg-slate-900 border border-slate-700 space-y-3">
                <div className="text-xs font-bold text-white">
                  Upload Corrected Verification Selfie
                </div>
                <label className="p-3 rounded-xl bg-slate-950 border border-slate-800 hover:border-purple-500 cursor-pointer flex flex-col gap-1 text-xs">
                  <span className="font-bold text-slate-300">
                    Replacement Selfie Photo *
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) readFileAsDataUrl(file, setReplaceSelfieDataUrl);
                    }}
                    className="text-[11px] text-slate-400"
                  />
                  {replaceSelfieDataUrl && (
                    <span className="text-[10px] text-emerald-400 font-mono">
                      ✓ Replacement selfie selected
                    </span>
                  )}
                </label>
                <div className="flex justify-end">
                  <button
                    type="button"
                    disabled={isSubmittingReplace}
                    onClick={() => handleResubmitReplacement('SELFIE', 'SELFIE')}
                    className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold cursor-pointer flex items-center gap-1.5"
                  >
                    {isSubmittingReplace ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    )}
                    <span>Resubmit Corrected Selfie for Review</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notification history list */}
        {notifications.map((notif) => (
          <div
            key={notif.id}
            className={`p-3 rounded-xl border text-xs flex flex-wrap items-start justify-between gap-2 ${
              notif.read
                ? 'bg-slate-950/60 border-slate-800 text-slate-400'
                : 'bg-rose-950/30 border-rose-500/40 text-slate-200'
            }`}
          >
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-bold text-white">{notif.title}</span>
                <span className="px-2 py-0.5 rounded bg-slate-800 text-rose-300 font-mono text-[10px]">
                  Item: {notif.documentLabel || notif.documentType}
                </span>
                <span className="text-[10px] text-slate-500 font-mono">
                  {new Date(notif.createdAt).toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-rose-300 font-bold">Rejection Reason: </span>
                <span className="text-white">{notif.rejectionReason}</span>
              </div>
            </div>

            {!notif.read && (
              <button
                type="button"
                onClick={() => handleMarkNotificationRead(notif.id)}
                className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] font-bold cursor-pointer"
              >
                Mark Read
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
